import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import type { FinancialChartDatum } from "../../types/financialUi"
import {
  FINANCIAL_CHART_RESIZE_DEBOUNCE_MS,
  hasFinancialChartData,
  sanitizeFinancialSeries,
} from "../../utils/financialChartUtils"
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion"
import { FinancialEmptyState } from "../FinancialEmptyState"

interface FinancialTimelineChartProps {
  data: readonly FinancialChartDatum[]
  primaryLabel: string
  secondaryLabel?: string
  ariaLabel: string
  emptyTitle: string
  emptyDescription?: string
  formatValue: (value: number) => string
  formatAxisValue?: (value: number) => string
}

export const FinancialTimelineChart: React.FC<FinancialTimelineChartProps> = ({
  data,
  primaryLabel,
  secondaryLabel,
  ariaLabel,
  emptyTitle,
  emptyDescription,
  formatValue,
  formatAxisValue = formatValue,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const chartData = sanitizeFinancialSeries(data)

  if (!hasFinancialChartData(chartData)) {
    return <FinancialEmptyState title={emptyTitle} description={emptyDescription} />
  }

  return (
    <div className="h-80 w-full min-w-0 xl:h-96" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%" debounce={FINANCIAL_CHART_RESIZE_DEBOUNCE_MS}>
        <AreaChart data={chartData} margin={{ top: 16, right: 16, left: 4, bottom: 4 }} accessibilityLayer>
          <defs>
            <linearGradient id="financialPrimaryGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f97316" stopOpacity={0.28} />
              <stop offset="95%" stopColor="#f97316" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="financialSecondaryGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.22} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 5" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
          <YAxis
            tickFormatter={(value: number) => formatAxisValue(value)}
            tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }}
            axisLine={false}
            tickLine={false}
            width={82}
          />
          <Tooltip
            formatter={(value, name) => [
              formatValue(Number(value ?? 0)),
              name === "secondaryValue" ? (secondaryLabel ?? primaryLabel) : primaryLabel,
            ]}
            cursor={{ stroke: "#fb923c", strokeDasharray: "4 4" }}
            contentStyle={{ borderRadius: 12, borderColor: "#e2e8f0", boxShadow: "0 10px 30px rgba(15,23,42,.10)" }}
          />
          <Legend formatter={(value) => (value === "secondaryValue" ? secondaryLabel : primaryLabel)} />
          <Area
            type="monotone"
            dataKey="value"
            name={primaryLabel}
            stroke="#f97316"
            strokeWidth={3}
            fill="url(#financialPrimaryGradient)"
            dot={{ r: 3, fill: "#ffffff", stroke: "#f97316", strokeWidth: 2 }}
            activeDot={{ r: 6, fill: "#f97316", stroke: "#ffffff", strokeWidth: 3 }}
            isAnimationActive={!prefersReducedMotion}
            animationDuration={950}
            animationEasing="ease-out"
          />
          {secondaryLabel && (
            <Area
              type="monotone"
              dataKey="secondaryValue"
              name={secondaryLabel}
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#financialSecondaryGradient)"
              dot={{ r: 2.5, fill: "#ffffff", stroke: "#3b82f6", strokeWidth: 2 }}
              activeDot={{ r: 5, fill: "#3b82f6", stroke: "#ffffff", strokeWidth: 3 }}
              isAnimationActive={!prefersReducedMotion}
              animationDuration={1050}
              animationEasing="ease-out"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
